/**
 * Lightweight first-party tracking client.
 *
 * - Generates a persistent anonymous ID stored in localStorage.
 * - Captures UTM/referrer attribution on first visit.
 * - Sends events to the `track-event` edge function in batches.
 * - Essential events (assessment_*, auth_*) always send so admins can debug.
 *   Attribution + activity events respect analytics consent.
 */

import { supabase } from "@/integrations/supabase/client";
import { getConsent } from "@/lib/consent";
import { getCachedIsAdmin, refreshIsAdmin } from "@/lib/isAdminUser";

const ANON_KEY = "tk_anon_id_v1";
const FIRST_TOUCH_KEY = "tk_first_touch_v1";
const SESSION_KEY = "tk_assessment_session_v1";

const ESSENTIAL_EVENT_PREFIXES = ["assessment_", "question_", "signup_", "login_", "password_", "email_verif", "identify"];

function isEssential(type: string) {
  return ESSENTIAL_EVENT_PREFIXES.some((p) => type.startsWith(p));
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getAnonymousId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

function parseUtm(): Record<string, string | null> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source"),
    utm_medium: p.get("utm_medium"),
    utm_campaign: p.get("utm_campaign"),
    utm_term: p.get("utm_term"),
    utm_content: p.get("utm_content"),
    gclid: p.get("gclid"),
    fbclid: p.get("fbclid"),
    msclkid: p.get("msclkid"),
  };
}

export function captureFirstTouch() {
  if (typeof window === "undefined") return;
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    const utm = parseUtm();
    const payload = {
      ...utm,
      referrer: document.referrer || null,
      landing_path: window.location.pathname + window.location.search,
      captured_at: new Date().toISOString(),
    };
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(payload));
  } catch { /* ignore */ }
}

export function getFirstTouch(): Record<string, unknown> | null {
  try {
    const v = localStorage.getItem(FIRST_TOUCH_KEY);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

export function getLastTouch(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  return {
    ...parseUtm(),
    referrer: document.referrer || null,
    landing_path: window.location.pathname + window.location.search,
    captured_at: new Date().toISOString(),
  };
}

// Assessment session helpers
export function getOrCreateAssessmentSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = uuid();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return uuid();
  }
}
export function resetAssessmentSession(): string {
  const id = uuid();
  try { sessionStorage.setItem(SESSION_KEY, id); } catch { /* ignore */ }
  return id;
}

// Event queue + flush
interface QueuedEvent {
  type: string;
  [k: string]: unknown;
}

const queue: QueuedEvent[] = [];
let flushTimer: number | null = null;

async function flush() {
  flushTimer = null;
  if (queue.length === 0) return;
  // Exclude admins - their browsing should not appear in analytics.
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
    queue.splice(0, queue.length);
    return;
  }
  const { data: { user } } = await supabase.auth.getUser();
  if (getCachedIsAdmin() || (user && await refreshIsAdmin())) {
    queue.splice(0, queue.length);
    return;
  }
  const batch = queue.splice(0, queue.length);
  try {
    await supabase.functions.invoke("track-event", { body: { events: batch } });
  } catch {
    // Swallow - tracking must never break the app.
  }
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(flush, 1200);
}

export function track(type: string, payload: Record<string, unknown> = {}) {
  try {
    if (!isEssential(type) && getConsent() !== "accepted") {
      return; // analytics events require consent
    }
    queue.push({
      type,
      anonymous_id: getAnonymousId(),
      ...payload,
    });
    scheduleFlush();
  } catch { /* ignore */ }
}

// Identify links anonymous_id to authenticated user + sends attribution.
export function identify() {
  const ft = getFirstTouch();
  track("identify", {
    attribution: { first_touch: ft, last_touch: getLastTouch() },
  });
}

// Flush on page hide so we don't lose in-flight events.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => { void flush(); });
}
